import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Popover from '@mui/material/Popover';
import { useState, useRef, useEffect, useCallback } from 'react';
import NRTag from '../NRTag/NRTag';
import { useTheme } from '@mui/material/styles';
import { capitalizeFirstLetter } from '../../utils/utils';
import debounce from 'lodash.debounce';

const NRTagList = ({ tags, maxRows = 1, maxWidth = 500, emptyPlaceholder = '-', showAll = false, isScope }) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const containerRef = useRef(null);
  const [visibleCount, setVisibleCount] = useState(showAll ? tags?.length || 0 : Math.min(tags?.length || 0, 1));
  const lastWidth = useRef(0);
  const isInitialRender = useRef(true);

  useEffect(() => {
    if (showAll) {
      setVisibleCount(tags?.length || 0);
      return;
    }
    if (isInitialRender.current) {
      isInitialRender.current = false;
      // Trigger initial calculation if we have width
      if (containerRef.current?.offsetWidth) {
        calculateVisibleTags(containerRef.current.offsetWidth);
      }
      return;
    }
    // Only reset if we don't have a width yet, otherwise wait for ResizeObserver or trigger manually
    if (containerRef.current?.offsetWidth) {
      calculateVisibleTags(containerRef.current.offsetWidth);
    } else {
      setVisibleCount(Math.min(tags?.length || 0, 1));
    }
  }, [tags, showAll]);

  const handlePopoverOpen = event => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const calculateVisibleTags = useCallback(
    debounce(containerWidth => {
      if (showAll) {
        setVisibleCount(tags?.length || 0);
        return;
      }
      if (!tags || tags.length === 0) {
        setVisibleCount(0);
        return;
      }
      if (containerWidth <= 0) return;

      const tagGap = 8;
      const moreTagPadding = 20;
      const moreTagGap = 8;
      const charWidth = 8.2; // Slightly more generous character width

      let currentWidth = 0;
      let fitsAll = true;

      // First pass: check if all tags fit without the "more" tag
      for (let i = 0; i < tags.length; i++) {
        const tagText = tags[i].value ? capitalizeFirstLetter(tags[i].value) : tags[i].name;
        const tagTextWidth = (tagText?.length || 0) * charWidth + 20;
        const totalTagWidth = tagTextWidth + (i > 0 ? tagGap : 0);

        if (currentWidth + totalTagWidth <= containerWidth) {
          currentWidth += totalTagWidth;
        } else {
          fitsAll = false;
          break;
        }
      }

      if (fitsAll) {
        setVisibleCount(tags.length);
      } else {
        // Second pass: calculate how many tags fit with the "more" tag
        // User requested: "show always at least 2 + de +X more"
        currentWidth = 0;
        let count = 0;

        for (let i = 0; i < tags.length; i++) {
          const remainingCount = tags.length - i;
          const moreTagText = `+${remainingCount} more...`;
          const moreTagWidth = moreTagText.length * charWidth + moreTagPadding;

          const tagText = tags[i].value ? capitalizeFirstLetter(tags[i].value) : tags[i].name;
          const tagTextWidth = (tagText?.length || 0) * charWidth + 20;
          const totalTagWidth = tagTextWidth + (i > 0 ? tagGap : 0);

          // If we haven't reached 2 tags yet, we keep adding them regardless of width
          if (count < 2 || currentWidth + totalTagWidth + moreTagGap + moreTagWidth <= containerWidth) {
            currentWidth += totalTagWidth;
            count++;
          } else {
            break;
          }
        }
        // Final check: if we are showing tags but not all of them,
        // ensure we don't accidentally show ALL tags if we reached the end but fitsAll was false
        // (though logic above should handle it)
        setVisibleCount(Math.max(0, count));
      }
    }, 100),
    [tags, showAll]
  );

  useEffect(() => {
    if (!containerRef.current || !tags || tags.length === 0 || showAll) return;

    const resizeObserver = new ResizeObserver(entries => {
      for (let entry of entries) {
        const width = entry.contentRect.width;
        // Only recalculate if width changed significantly (more than 15px)
        if (Math.abs(width - lastWidth.current) > 15) {
          lastWidth.current = width;
          calculateVisibleTags(width);
        }
      }
    });

    resizeObserver.observe(containerRef.current);

    // Initial calculation
    const initialWidth = containerRef.current.offsetWidth;
    if (initialWidth > 0) {
      lastWidth.current = initialWidth;
      calculateVisibleTags(initialWidth);
    }

    return () => {
      resizeObserver.disconnect();
      calculateVisibleTags.cancel();
    };
  }, [tags, calculateVisibleTags]);

  if (!tags || tags.length === 0) return <Typography variant="body2">{emptyPlaceholder}</Typography>;

  const visibleTags = showAll ? tags : tags.slice(0, visibleCount);
  const remainingTags = showAll ? [] : tags.slice(visibleCount);

  return (
    <Box
      ref={containerRef}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        width: '100%',
        overflow: 'hidden'
      }}
    >
      {visibleTags.map((tag, index) => (
        <NRTag
          key={index}
          text={tag.value ? capitalizeFirstLetter(tag.value) : tag.name}
          tooltipContent={tag.description}
          isScope={isScope}
          actualScope={tag.actualScope}
        />
      ))}
      {remainingTags.length > 0 && (
        <>
          <Box
            onClick={handlePopoverOpen}
            sx={{
              display: 'flex',
              padding: theme.spacing(0.5, 1),
              alignItems: 'center',
              gap: theme.spacing(0.5),
              borderRadius: theme.spacing(0.5),
              background: theme.palette.divider,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              height: 24,
              boxSizing: 'border-box',
              flexShrink: 0
            }}
          >
            <Typography variant="caption" color="textSecondary">
              {`+${remainingTags.length} more...`}
            </Typography>
          </Box>
          <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={handlePopoverClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left'
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left'
            }}
            sx={{
              pointerEvents: 'auto'
            }}
            disableRestoreFocus
            PaperProps={{
              sx: {
                borderRadius: theme.spacing(1),
                boxShadow: theme.shadows[3],
                backgroundColor: theme.palette.background.paper
              }
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                padding: theme.spacing(1, 2),
                alignItems: 'center',
                gap: 1,
                maxWidth: maxWidth,
                p: 2
              }}
            >
              {remainingTags.map((tag, index) => (
                <NRTag
                  key={index}
                  text={tag.value ? capitalizeFirstLetter(tag.value) : tag.name}
                  tooltipContent={tag.description}
                  isScope={isScope}
                  actualScope={tag.actualScope}
                />
              ))}
            </Box>
          </Popover>
        </>
      )}
    </Box>
  );
};

export default NRTagList;
